import type {
  CarrierByIdForViewInterface,
  CarrierForEditResponse,
  ProcessedBasicCostInterface,
  ProcessedMoreInfoInterface,
} from '@/types/carrier-types';
import type { RecursiveNullable } from '@/types/component';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { processBasicAndCost, processMoreInfo } from './utils';
import type { StoreBaseResponse } from '@/types/types';

type NullableViewCarrierType = RecursiveNullable<{
  data: CarrierByIdForViewInterface;
  processedData: {
    basicCost: ProcessedBasicCostInterface | null;
    moreInfo: ProcessedMoreInfoInterface | null;
  };
}>;

interface CarrierReduxStateInterface {
  viewCarrierById: NullableViewCarrierType;
  editCarrierById: RecursiveNullable<{
    data: CarrierForEditResponse | null;
    isLoading: boolean;
    isFetching: boolean;
  }>;
}

const initialState = {
  viewCarrierById: {
    data: null,
    processedData: {
      basicCost: null,
      moreInfo: null,
    },
  },
  editCarrierById: {
    data: null,
    isLoading: false,
    isFetching: false,
  },
} as CarrierReduxStateInterface;

const carrierSlice = createSlice({
  name: 'editCarrier',
  initialState,
  reducers: {
    setViewCarrierById: (
      state,
      action: PayloadAction<StoreBaseResponse<CarrierByIdForViewInterface>>,
    ) => {
      const data = action.payload?.data;
      let processedData = null;
      if (data) {
        processedData = {
          basicCost: processBasicAndCost(data),
          moreInfo: processMoreInfo(data),
        };
      }
      if (data) {
        state.viewCarrierById = {
          data,
          processedData,
        };
      }
    },
    setEditCarrierById: (
      state,
      action: PayloadAction<
        RecursiveNullable<{
          data: StoreBaseResponse<CarrierForEditResponse>;
          isLoading: boolean;
          isFetching: boolean;
        }>
      >,
    ) => {
      const data = action.payload?.data?.data;
      const isLoading = action.payload?.isLoading;
      const isFetching = action.payload?.isFetching;
      if (data) {
        state.editCarrierById.data = data;
      }
      if (typeof isLoading === 'boolean') {
        state.editCarrierById.isLoading = isLoading;
      }
      if (typeof isFetching === 'boolean') {
        state.editCarrierById.isFetching = isFetching;
      }
    },
    resetCarrierSlice: () => initialState,
  },
});

export const carrierActions = carrierSlice.actions;

export const carrierReducers = carrierSlice.reducer;
